// `TaskExpired` is emitted by AIGC/MT when a task's TTL is exceeded. Per
// the AgenticOnboarding design doc the canonical wire form is an HTTP 400
// (handled by the .catch branches below as `onFailed`), but we also union
// it into the body-status enum so a future MT change that surfaces it
// inline in the response body doesn't silently fall into the InProgress
// branch and spin the analyzing view forever. AB#10747343.
export type TaskStatus = 'InProgress' | 'Completed' | 'Failed' | 'TaskExpired';

export interface TaskPollData {
  Status?: TaskStatus;
  [key: string]: unknown;
}

export interface TaskStatusPollerConfig {
  pollTask: () => Promise<unknown>;
  onInProgress?: (data: TaskPollData) => void;
  onCompleted: (data: TaskPollData) => void;
  onFailed: (data?: TaskPollData) => void;
  interval?: number;
}

export interface TaskStatusPoller {
  start: () => void;
  stop: () => void;
}

const extract = (result: unknown): TaskPollData => {
  if (result == null) return {};
  const r = result as any;
  const raw = r.data !== undefined ? r.data : r;
  return (raw?.Data ?? raw ?? {}) as TaskPollData;
};

export const createTaskStatusPoller = ({
  pollTask,
  onInProgress,
  onCompleted,
  onFailed,
  interval = 2000,
}: TaskStatusPollerConfig): TaskStatusPoller => {
  let timerId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  let pollInFlight = false;

  const stopTimer = () => {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const fail = (data?: TaskPollData) => {
    if (stopped) return;
    stopped = true;
    stopTimer();
    onFailed(data);
  };

  const handleResult = (data: TaskPollData) => {
    const status = data?.Status;
    if (status === 'Completed') {
      stopTimer();
      stopped = true;
      onCompleted(data);
    } else if (status === 'Failed' || status === 'TaskExpired') {
      // TaskExpired is a terminal state — same teardown as Failed.
      // Caller's onFailed surfaces the error view; the existing "Try again"
      // button on the snapshot-review error view already clears the stale
      // snapshotTaskId and routes back to Step 1 so a fresh task can be
      // generated.
      fail(data);
    } else if (onInProgress) {
      onInProgress(data);
    }
  };

  const poll = () => {
    if (stopped || pollInFlight) return;
    pollInFlight = true;

    pollTask().then((result) => {
      pollInFlight = false;
      if (stopped) return;
      handleResult(extract(result));
    }).catch(() => {
      pollInFlight = false;
      fail();
    });
  };

  const start = () => {
    if (stopped || timerId !== null) return;
    poll();
    timerId = setInterval(poll, interval);
  };

  const stop = () => {
    stopped = true;
    stopTimer();
  };

  return { start, stop };
};
